import { Component, OnInit, OnDestroy, ViewChild, HostListener, Input, AfterViewInit, ViewChildren, Output, EventEmitter } from '@angular/core';
import * as Chart from 'chart.js';
import { ActivatedRoute, Router } from '@angular/router';
import { DatePipe } from '@angular/common';
import { PaygService } from '../../../services/payg.service';
import { PaygPaymentTypeModel } from '../../../models/payg/payg-payment-type.model';
import { getQueryParamFromAncestorRoute, getParamFromAncestorRoute } from '../../../utilities/route.utilities';

@Component({
    selector: 'payment-type-chart',
    template: '<canvas id="basechart" #basechart></canvas>',
    styles: [':host { width: 100%; position: relative; display:block; }'],
    host: { 'class': 'block-detail' }
})
export class PaymentsByTypeChartComponent implements AfterViewInit, OnDestroy {

    @ViewChildren('basechart') basechart: any;
    @Input() data: PaygPaymentTypeModel[];
    public paymentByType: PaygPaymentTypeModel[] = [];
    chart: any;
    ctx: any;
    public subscriptions: any[] = [];
    debounceTimeout = 2000;
    debounceId: any;
    chartData: any;
    heatGradientStroke: any;
    hotWaterGradientStroke: any;
    coldWaterGradientStroke: any;
    pipe: DatePipe = new DatePipe('en_GB');
    clientId: number;
    timePeriod: number = 12;
    siteId: number;

    hasPayPoint: boolean = false;
    hasSwitch2App: boolean = false;
    hasWebsite: boolean = false;
    hasAllPay: boolean = false;

    @Input() hasData: boolean = false;
    @Output() hasDataChange = new EventEmitter<boolean>();

    @Input() hasLoading: boolean = true;
    @Output() hasLoadingChange = new EventEmitter<boolean>();

    constructor(
        private paygService: PaygService,
        private route: ActivatedRoute,
        private router: Router
    ) { }

    ngAfterViewInit(): void {
        this.route.queryParams.subscribe(queryParams => {
            this.onInitialise();
        });
    }

    onInitialise() {

        this.clientId = getParamFromAncestorRoute(this.route, 'clientId');
        this.siteId = getQueryParamFromAncestorRoute(this.route, 'siteId');
        this.timePeriod = getQueryParamFromAncestorRoute(this.route, 'timePeriod') == null ? 12 : getQueryParamFromAncestorRoute(this.route, 'timePeriod');

        this.heatGradientStroke = this.basechart.first.nativeElement.getContext('2d').createLinearGradient(500, 0, 100, 0);
        this.heatGradientStroke.addColorStop(0, '#668ac1');
        this.heatGradientStroke.addColorStop(1, '#39547f');

        this.hotWaterGradientStroke = this.basechart.first.nativeElement.getContext('2d').createLinearGradient(500, 0, 100, 0);
        this.hotWaterGradientStroke.addColorStop(0, '#668ac1');
        this.hotWaterGradientStroke.addColorStop(1, '#39547f');

        this.coldWaterGradientStroke = this.basechart.first.nativeElement.getContext('2d').createLinearGradient(500, 0, 100, 0);
        this.coldWaterGradientStroke.addColorStop(0, '#668ac1');
        this.coldWaterGradientStroke.addColorStop(1, '#39547f');

        this.search();
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
        if (this.debounceId) {
            clearTimeout(this.debounceId);
        }
    }

    @HostListener('window:resize', ['$event'])
    onResize(event: any) {
        this.createChart();
    }

    search() {
        this.paygService.getPaymentByType(this.clientId, this.siteId, this.timePeriod.toString()).then().then((result) => {
            this.paymentByType = result;
            this.chartData = this.processData(this.paymentByType);
            this.hasDataChange.emit(this.chartData && this.chartData.labels.length > 0);
            this.hasLoadingChange.emit(!this.chartData || this.chartData.labels.length == 0);
            this.createChart();
        }).catch((reason) => {
            this.hasDataChange.emit(this.chartData);
            this.hasLoadingChange.emit(this.chartData);
        });
    }

    processData(data: PaygPaymentTypeModel[]) {

        let monthCount: number = 0;
        this.hasPayPoint = false;
        this.hasSwitch2App = false;
        this.hasWebsite = false;
        this.hasAllPay = false;

        let graphData = {
            labels: [],
            payPoint: [],
            switch2App: [],
            webSite: [],
            allPay: []
        };

        if (data && data.length > 0) graphData.labels.push(this.pipe.transform(data[0].date, 'MMM'))

        var initializeMonth = function () {
            graphData.payPoint.push(0);
            graphData.switch2App.push(0);
            graphData.webSite.push(0);
            graphData.allPay.push(0);
        }

        initializeMonth();

        for (var i = 0; i < data.length; i++) {

            switch (data[i].paymentMethod.toUpperCase()) {
                case 'PAYPOINT':
                    graphData.payPoint[monthCount] = data[i].amount;
                    this.hasPayPoint = true;
                    break;
                case 'SWITCH2 APP':
                    graphData.switch2App[monthCount] = data[i].amount;
                    this.hasSwitch2App = true;
                    break;
                case 'SWITCH2 WEBSITE':
                    graphData.webSite[monthCount] = data[i].amount;
                    this.hasWebsite = true;
                    break;
                case 'ALL PAY':
                    graphData.allPay[monthCount] = data[i].amount;
                    this.hasAllPay = true;
                    break;
            }

            if (i + 1 < data.length && data[i].date != data[i + 1].date) {
                graphData.labels.push(this.pipe.transform(data[i + 1].date, 'MMM'))
                initializeMonth();
                monthCount++;
            }

        }

        return (graphData);
    }

    private getChartConfig(): any {
        let config = {
            type: 'bar',
            options: {
                tooltips: {
                    mode: 'index',
                    enabled: true,
                    backgroundColor: '#ffffff',
                    titleFontColor: '#153954',
                    bodyFontColor: '#153954',
                    borderWidth: 2,
                    borderColor: '#009aa3',
                    callbacks: {
                        label: function (tooltipItem, data) {
                            var allData: any = data != null && data.datasets != null && tooltipItem != null && tooltipItem.datasetIndex != null ? data.datasets[tooltipItem.datasetIndex].data : [];
                            var hoverData: any = data != null && data.datasets != null && tooltipItem != null && tooltipItem.datasetIndex != null ? data.datasets[tooltipItem.datasetIndex] : [];
                            var tooltipLabel: any = data != null && data.labels != null && tooltipItem != null && tooltipItem.index != null ? data.labels[tooltipItem.index] : [];
                            var tooltipData: any = allData != null && allData.length > 0 && tooltipItem != null && tooltipItem.index != null ? allData[tooltipItem.index] : [];

                            return hoverData.label + ' £' + tooltipData;
                        }
                    }
                },
                scales: {
                    xAxes: [{
                        stacked: true,
                        barThickness: 20,
                        scaleLabel: {
                            display: true,
                            labelString: 'Month',
                            fontSize: 15,
                            fontStyle: "bold",
                        },
                        gridLines: {
                            display: false
                        }
                    }],
                    yAxes: [
                        {
                            stacked: true,
                            id: "y-axis-0",
                            position: 'left',
                            scaleLabel: {
                                display: true,
                                labelString: 'Value (£)',
                                fontSize: 15,
                                fontStyle: "bold",
                            },
                            gridLines: {
                                display: true
                            }
                        },
                    ]

                }
            }
        }

        return config;
    }

    createChart() {
        if (this.chart != null) this.chart.destroy();
        if (!this.basechart) return;

        let datasets = [] as any;

    if (this.hasPayPoint) {
        datasets.push({
            type: 'bar',
            label: 'PayPoint',
            yAxisID: 'y-axis-0',
            data: this.chartData.payPoint,
            backgroundColor: '#153954'
        });
    }
    
    if (this.hasSwitch2App) {
        datasets.push({
            type: 'bar',
            label: 'Switch2 App',
            yAxisID: 'y-axis-0',
            data: this.chartData.switch2App,
            backgroundColor: '#9eb3be'
        });
    }

        if (this.hasWebsite) {
            datasets.push({
                type: 'bar',
                label: 'Website',
                yAxisID: 'y-axis-0',
                data: this.chartData.webSite,
                backgroundColor: '#00539f'
            });
        }

        if (this.hasAllPay) {
            datasets.push({
                type: 'bar',
                label: 'All Pay',
                yAxisID: 'y-axis-0',
                data: this.chartData.allPay,
                backgroundColor: '#009aa3'
            });
        }

        let config = this.getChartConfig();
        let chartConfiguration: Chart.ChartConfiguration = {
            type: config.type,
            options: config.options,
            data: {
                labels: this.chartData.labels,
                datasets: datasets
            }
        };

        if (this.debounceId) {
            window.clearTimeout(this.debounceId);
        };

        this.debounceId = window.setTimeout(() => {
            if (this.chart != null) this.chart.destroy();
            let ctx = this.basechart.first.nativeElement;
            this.chart = new Chart(ctx, chartConfiguration);
        }, this.debounceTimeout);
    }

}
